import React from 'react';
import { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useWizardContext, WizardFlowActionType } from './WizardContext';
import { useStore } from '../../../store';
import { ChangePersonalAddressData, ErrorData } from '../ChangePersonalAddress';
import { ChangePersonalAddressFlow } from '../ChangePersonalAddressSteps';
import { ChangePersonalAddressEnum, ChangePersonalAddressKeys } from './ChangePersonalAddressSchema';

export enum errorCodesEnum {
  findAddress = 'findAddress',
  P_O = 'P.O',
  city = 'city',
  state = 'state',
  zip = 'zip',
  inlineAddress = 'inlineAddress',
  default = 'default',
}
interface useStepHookProps {
  setError?: UseFormSetError<{
    street: string;
    apt: string;
    city: string;
    state: string;
    zip: string;
  }>;
}
export const useChangeAddressStepHook = ({ setError }: useStepHookProps) => {
  const { t } = useTranslation();
  const { customerId } = useStore((state) => state.user);
  const { step, data, dispatch } = useWizardContext() as {
    step: number;
    data: ChangePersonalAddressData;
    dispatch: React.Dispatch<Partial<ChangePersonalAddressData>> | any;
  };
  const summary = useStore((state) => state.summary);
  const {
    customer: { nationalIdDescription, email },
  } = summary;

  const INLINE_ERROR_CODES = [4043, 4045, 4046, 4052, 4046];

  type InlineErrorCode = (typeof INLINE_ERROR_CODES)[number];

  // typeguard for inline error code
  const isInlineErrorCode = (code: number): code is InlineErrorCode =>
    INLINE_ERROR_CODES.includes(code as InlineErrorCode);

  const ERROR_CODES = [40067, 4066, 9999, 4043, 41197];

  // typeguard for error code
  type ErrorCode = (typeof ERROR_CODES)[number];
  const isErrorCode = (code: number): code is ErrorCode => ERROR_CODES.includes(code);

  const errorMapping = new Map<number, { errorKey: errorCodesEnum; formInput: ChangePersonalAddressKeys | null }>([
    [40067, { errorKey: errorCodesEnum.findAddress, formInput: ChangePersonalAddressEnum.street }],
    [4066, { errorKey: errorCodesEnum.P_O, formInput: ChangePersonalAddressEnum.street }],
    [41197, { errorKey: errorCodesEnum.P_O, formInput: ChangePersonalAddressEnum.street }],

    [4043, { errorKey: errorCodesEnum.city, formInput: ChangePersonalAddressEnum.city }],
    [4045, { errorKey: errorCodesEnum.state, formInput: ChangePersonalAddressEnum.state }],
    [4046, { errorKey: errorCodesEnum.zip, formInput: ChangePersonalAddressEnum.zip }],
    [4052, { errorKey: errorCodesEnum.inlineAddress, formInput: ChangePersonalAddressEnum.street }],
    [9999, { errorKey: errorCodesEnum.default, formInput: null }],
  ]);

  const getErrorByCode = (code: string): { errorKey: errorCodesEnum; formInput: ChangePersonalAddressKeys | null } => {
    const error = errorMapping.get(parseInt(code, 10)) || errorMapping.get(9999);
    if (!error) {
      throw new Error('Default error mapping not found');
    }
    return error;
  };

  const handleSetError = (error: ErrorData) => {
    if (!error.errorCode) return;
    dispatch({
      type: WizardFlowActionType.SET_DATA,
      payload: { error },
    });
    dispatch({
      type: WizardFlowActionType.SET_STEP,
      payload: ChangePersonalAddressFlow.ERROR,
    });
  };

  const handleInlineError = (errorKey: errorCodesEnum, errorCode: number, inputField: ChangePersonalAddressKeys) => {
    dispatch({
      type: WizardFlowActionType.SET_DATA,
      payload: {
        error: {
          errorKey,
          errorCode,
          inputField,
          inlineError: true,
          errorMessage: t(`CHANGE_ADDRESS.ERRORS.${errorKey}.description`),
        },
      },
    });
    dispatch({
      type: WizardFlowActionType.SET_STEP,
      payload: ChangePersonalAddressFlow.AddressForm,
    });
    if (setError) {
      setError(inputField, {
        type: 'manual',
        message: t(`CHANGE_ADDRESS.ERRORS.${errorKey}.description`),
      });
    }
  };

  const handleErrorCode = (error: any) => {
    if (!error.code) return;
    const errorObj = getErrorByCode(error?.code?.toString());

    const { code: errorCode } = error;
    const { errorKey } = errorObj;
    if (!errorKey || !errorCode) return;

    if (isInlineErrorCode(errorCode)) {
      const inputField = errorObj.formInput as ChangePersonalAddressKeys;

      handleInlineError(errorKey as errorCodesEnum, errorCode, inputField);
    } else if (isErrorCode(errorCode)) {
      handleSetError({
        errorCode,
        errorKey,
        inlineError: false,
        prevStep: step,
      });
    } else {
      handleSetError({
        errorCode: 9999,
        errorKey,
        inlineError: false,
        prevStep: step,
      });
    }
  };

  const handleForward = () => {
    dispatch({ type: WizardFlowActionType.NEXT_STEP });
  };
  const handleSuccess = () => {
    handleForward();
  };
  const handleEdit = () => {
    dispatch({ type: WizardFlowActionType.PREV_STEP });
  };
  return {
    step,
    data,
    t,
    dispatch,
    nationalIdDescription,
    handleForward,
    email,
    customerId,
    handleErrorCode,
    handleSuccess,
    handleEdit,
  };
};
